import { useDebounceValue } from 'noddi-util';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { FilterControl, NumberRange, NumberRangeControl } from '../../../../../types/filterControl';
import { NoddiInput } from '../../../../Inputs/NoddiInput';
import { IncludeExcludeFields } from './IncludeExcludeFields';

type RangeValue = NumberRange | undefined;

const NumberRangeInputs = ({
  value,
  onChange,
  minLabel,
  maxLabel,
  minPlaceholder,
  maxPlaceholder
}: {
  value: RangeValue;
  onChange: (value: RangeValue) => void;
  minLabel?: string;
  maxLabel?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
}) => {
  const [localMin, setLocalMin] = useState<string>(value?.min?.toString() ?? '');
  const [localMax, setLocalMax] = useState<string>(value?.max?.toString() ?? '');

  const debouncedMin = useDebounceValue({ value: localMin, delay: 500 });
  const debouncedMax = useDebounceValue({ value: localMax, delay: 500 });

  const commitRange = (min: string, max: string) => {
    const minValue = min === '' ? undefined : Number(min);
    const maxValue = max === '' ? undefined : Number(max);

    const currentMin = value?.min;
    const currentMax = value?.max;

    if (minValue !== currentMin || maxValue !== currentMax) {
      if (minValue === undefined && maxValue === undefined) {
        onChange(undefined);
      } else {
        onChange({ min: minValue, max: maxValue });
      }
    }
  };

  useEffect(() => {
    // A staggered debounce must not overwrite the other bound with an older value.
    if (debouncedMin === localMin && debouncedMax === localMax) {
      commitRange(debouncedMin, debouncedMax);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires on debounced input only; adding commitRange would re-emit the stale input after an external clear
  }, [debouncedMin, debouncedMax]);

  // Sync local state when value changes externally (e.g. clear). Adjust during render
  // rather than in an effect to avoid a cascading re-render.
  const selectedMin = value?.min;
  const selectedMax = value?.max;
  const [syncedRange, setSyncedRange] = useState({ min: selectedMin, max: selectedMax });
  if (syncedRange.min !== selectedMin || syncedRange.max !== selectedMax) {
    setSyncedRange({ min: selectedMin, max: selectedMax });
    setLocalMin(selectedMin?.toString() ?? '');
    setLocalMax(selectedMax?.toString() ?? '');
  }

  return (
    <div className='grid grid-cols-2 gap-1.5'>
      <NoddiInput
        type='number'
        label={minLabel ?? 'Min'}
        placeholder={minPlaceholder ?? 'Min'}
        value={localMin}
        onChange={(e) => setLocalMin(e.target.value)}
        // Commit before the drawer unmounts; a pending debounce would lose the typed range.
        onBlur={() => commitRange(localMin, localMax)}
        className='min-w-0'
      />
      <NoddiInput
        type='number'
        label={maxLabel ?? 'Max'}
        placeholder={maxPlaceholder ?? 'Max'}
        value={localMax}
        onChange={(e) => setLocalMax(e.target.value)}
        onBlur={() => commitRange(localMin, localMax)}
        className='min-w-0'
      />
    </div>
  );
};

export const NumberRangeFilter = ({
  control,
  renderFilterTitle
}: {
  control: NumberRangeControl;
  renderFilterTitle: (control: FilterControl) => ReactNode;
}) => {
  const { value, onChange } = control;
  // Include and exclude debounce apart; when both fire before a render, the second must build on the first.
  const latest = useRef(value);
  useLayoutEffect(() => {
    latest.current = value;
  }, [value]);
  const change = (next: Partial<typeof value>) => {
    latest.current = { ...latest.current, ...next };
    onChange(latest.current);
  };
  const labels = {
    minLabel: control.minLabel,
    maxLabel: control.maxLabel,
    minPlaceholder: control.minPlaceholder,
    maxPlaceholder: control.maxPlaceholder
  };

  return (
    <IncludeExcludeFields
      className='flex max-w-35 flex-col gap-2'
      title={renderFilterTitle(control)}
      include={<NumberRangeInputs value={value.include} onChange={(include) => change({ include })} {...labels} />}
      exclude={
        control.canExclude ? (
          <NumberRangeInputs value={value.exclude} onChange={(exclude) => change({ exclude })} {...labels} />
        ) : undefined
      }
    />
  );
};
